import { useCallback, useEffect, useState } from 'react';

interface GeolocationState {
	latitude: number | null;
	longitude: number | null;
	accuracy: number | null;
	error: string | null;
	loading: boolean;
}

const useGeolocation = () => {
	const [state, setState] = useState<GeolocationState>({
		latitude: null,
		longitude: null,
		accuracy: null,
		error: null,
		loading: true,
	});

	const getPosition = useCallback(() => {
		if (!navigator.geolocation) {
			setState((prev) => ({
				...prev,
				loading: false,
				error: 'Geolocation is not supported by your browser.',
			}));
			return;
		}

		setState((prev) => ({ ...prev, loading: true, error: null }));

		navigator.geolocation.getCurrentPosition(
			(position) => {
				setState({
					latitude: position.coords.latitude,
					longitude: position.coords.longitude,
					accuracy: position.coords.accuracy,
					error: null,
					loading: false,
				});
			},
			(err) => {
				let message = 'Unable to determine your location.';
				switch (err.code) {
					case err.PERMISSION_DENIED:
						message = 'Location access was denied. Please enable location permissions in your browser settings.';
						break;
					case err.POSITION_UNAVAILABLE:
						message = "Unable to determine your location. Please check your device's location services.";
						break;
					case err.TIMEOUT:
						message = 'Location request timed out. Please try again.';
						break;
				}
				setState((prev) => ({
					...prev,
					loading: false,
					error: message,
				}));
			},
			{
				enableHighAccuracy: true,
				timeout: 15000,
				maximumAge: 0,
			}
		);
	}, []);

	useEffect(() => {
		getPosition();
	}, [getPosition]);

	return {
		...state,
		retry: getPosition,
	};
};

export default useGeolocation;
